import { useState, useEffect } from "react";
import { Phone, Heart, X, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function SOSButton() {
  const [open, setOpen] = useState(false);
  const [crisisLines, setCrisisLines] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (open && crisisLines.length === 0) {
      api.get("/crisis-lines").then((r) => setCrisisLines(r.data)).catch(() => {});
    }
  }, [open, crisisLines.length]);

  const trusted = user?.trusted_contacts || [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="sos-trigger-button"
        className="fixed bottom-20 md:bottom-8 right-4 md:right-8 z-40 w-16 h-16 md:w-20 md:h-20 rounded-full bg-kokowai text-muka shadow-[0_8px_30px_rgba(139,58,51,0.4)] hover:scale-105 active:scale-95 transition-transform flex flex-col items-center justify-center gap-0.5"
        aria-label="Get support now"
      >
        <Heart size={20} strokeWidth={1.8} fill="#FAF8F5" />
        <span className="text-xs font-bold tracking-wider">SOS</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-muka border-border" data-testid="sos-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-charcoal">
              Kei konei mātou — we are here
            </DialogTitle>
            <DialogDescription className="text-charcoal/70 leading-relaxed">
              You are not alone in this moment. Pick what feels right. There is no wrong door.
            </DialogDescription>
          </DialogHeader>

          {trusted.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Your people
              </div>
              <div className="space-y-2">
                {trusted.map((c, i) => (
                  <a
                    key={i}
                    href={`tel:${c.phone}`}
                    data-testid={`sos-trusted-${i}`}
                    className="flex items-center gap-3 p-4 rounded-2xl bg-bone hover:bg-clay/30 transition-colors border border-border"
                  >
                    <div className="w-10 h-10 rounded-full bg-ngahere text-muka flex items-center justify-center font-serif">
                      {c.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-charcoal">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.relationship || "Trusted contact"} · {c.phone}
                      </div>
                    </div>
                    <Phone size={18} className="text-ngahere" />
                  </a>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-2">
              <AlertCircle size={12} /> Crisis lines · Aotearoa
            </div>
            <div className="space-y-2">
              {crisisLines.map((c, i) => (
                <a
                  key={i}
                  href={`tel:${c.phone.replace(/\s/g, "")}`}
                  data-testid={`sos-crisis-${i}`}
                  className="flex items-center justify-between p-4 rounded-2xl bg-bone hover:bg-kokowai/10 transition-colors border border-border"
                >
                  <div>
                    <div className="font-medium text-charcoal">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.note}</div>
                  </div>
                  <div className="font-serif text-lg text-kokowai">{c.phone}</div>
                </a>
              ))}
            </div>
          </section>

          <button
            onClick={() => setOpen(false)}
            data-testid="sos-close"
            className="mt-2 text-sm text-muted-foreground hover:text-charcoal flex items-center gap-1 mx-auto"
          >
            <X size={14} /> close
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
